import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, Paper, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DEV_SWITCHER_OFFSET } from '@/components/dev-user-switcher';
import { useAuth } from '@/contexts/auth';

export const Route = createFileRoute('/sign-in')({
  component: SignInPage,
});

function SignInFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      style={{
        minHeight: `calc(100dvh - ${DEV_SWITCHER_OFFSET})`,
        background: 'var(--cd-signin-gradient)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--mantine-spacing-md)',
      }}
    >
      <Paper radius="lg" p="xl" shadow="lg" maw={400} w="100%">
        <Stack gap="lg">
          <Stack align="center" gap={4}>
            <Box
              w={40}
              h={40}
              mb="xs"
              style={{
                background: 'linear-gradient(135deg, var(--mantine-color-indigo-6), var(--mantine-color-violet-6))',
                borderRadius: 'var(--mantine-radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
                fontSize: '16px',
              }}
            >
              CD
            </Box>
            <Title order={2}>CallDominator</Title>
            <Text c="dimmed" size="sm">
              Sign in to your account
            </Text>
          </Stack>
          {children}
        </Stack>
      </Paper>
    </Box>
  );
}

function DevSignIn() {
  return (
    <Text c="dimmed" size="sm" ta="center">
      Development mode — pick a user from the switcher at the top of the page.
    </Text>
  );
}

function KeycloakSignIn() {
  return (
    <Text c="dimmed" size="sm" ta="center">
      Redirecting to sign-in…
    </Text>
  );
}

function UnauthorizedSignIn({ onSignOut }: { onSignOut: () => void }) {
  return (
    <Stack gap="md">
      <Text c="red" size="sm" ta="center">
        This account is not provisioned for CallDominator or has been deactivated. Contact an administrator, or sign in
        with a different account.
      </Text>
      <Button fullWidth onClick={onSignOut}>
        Sign in with a different account
      </Button>
    </Stack>
  );
}

function UnconfiguredSignIn() {
  return (
    <Text c="dimmed" size="sm" ta="center">
      No authentication is configured for this environment.
    </Text>
  );
}

function SignInPage() {
  const { mode, user, error, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) void navigate({ to: '/' });
  }, [user, navigate]);

  return (
    <SignInFrame>
      {error === 'unauthorized' && <UnauthorizedSignIn onSignOut={signOut} />}
      {mode === 'dev' && <DevSignIn />}
      {mode === 'keycloak' && error === null && <KeycloakSignIn />}
      {mode === 'none' && <UnconfiguredSignIn />}
    </SignInFrame>
  );
}
